import { TableCell } from '@mui/material';
import clsx from 'clsx';

import AdvancedAnalysisItemTag from '../../../../../../../../../../../../Components/AdvancedAnalysisItemTag/AdvancedAnalysisItemTag';
import { AdvancedAnalysisItemType, ComplianceOperationItemFieldsFragment, EvaluationSheetResultItemFieldsFragment } from '../../../../../../../../../../../../generated/core-api/graphql-react-query';
import useRatioFormat from '../../../../../../../../../../../../Hooks/useRatioFormat';
import TopicTag from '../../../../../../../CommandPage/Components/CommandItem/Components/TopicTag/TopicTag';
import ComplianceOperationAccordion from './ComplianceOperationLogic/ComplianceOperationAccordion';
import FactCheckRunDetailsAccordion from './FactCheckRunDetailsAccordion';
import StyledRow from './styles';

interface SessionEvaluationSheetTableRowProps {
  advancedAnalysisMap: Map<string, { name: string; type?: AdvancedAnalysisItemType | null }>;
  complianceOperationMap: Map<string, Pick<ComplianceOperationItemFieldsFragment, 'operator' | 'title'>>;
  item: EvaluationSheetResultItemFieldsFragment;
  index: number;
  groupLength: number;
  moduleName?: string;
  topicMap: Map<string, { text: string; color?: string | null }>;
}

export default function SessionEvaluationSheetTableRow(props: SessionEvaluationSheetTableRowProps) {
  const {
    advancedAnalysisMap,
    complianceOperationMap,
    item,
    index,
    groupLength,
    moduleName,
    topicMap,
  } = props;
  const totalFormat = useRatioFormat();

  const displayCriticity = (() => {
    if (item.num !== undefined && item.denom !== undefined && item.num !== null && item.denom !== null) {
      if ((item.num / item.denom) < 0.5) { return item.criticity; }
      return undefined;
    } return undefined;
  })();

  if (item.__typename === 'EvaluationSheetResultAdvancedAnalysisItem') {
    return (
      <StyledRow key={item.id}>
        {index === 0 && (
          <TableCell rowSpan={groupLength} width={200}>
            {moduleName}
          </TableCell>
        )}
        <TableCell
          width={150}
          align="right"
          className={clsx('advancedAnalysisItem', { [item.criticity]: displayCriticity })}
        >
          <AdvancedAnalysisItemTag displayText={item.advancedAnalysisItem.name} type={item.advancedAnalysisItem.type} />
        </TableCell>
        <TableCell width={100} align="right" className={`${displayCriticity}`}>
          {totalFormat.format({ numerator: item.num ?? 0, denominator: item.denom ?? 0 })}
        </TableCell>
      </StyledRow>
    );
  }

  if (item.__typename === 'EvaluationSheetResultTopicItem') {
    return (
      <StyledRow key={item.id}>
        {index === 0 && (
          <TableCell rowSpan={groupLength} width={200}>
            {moduleName}
          </TableCell>
        )}
        <TableCell
          width={150}
          align="right"
          className={clsx({ [item.criticity]: displayCriticity })}
        >
          <TopicTag topic={item.topic.text} color={item.topic.color} showIcon />
        </TableCell>
        <TableCell width={100} align="right" className={`${displayCriticity}`}>
          {totalFormat.format({ numerator: item.num ?? 0, denominator: item.denom ?? 0 })}
        </TableCell>
      </StyledRow>
    );
  }

  if (item.__typename === 'EvaluationSheetResultComplianceOperationItem') {
    return (
      <StyledRow key={item.id}>
        {index === 0 && (
          <TableCell rowSpan={groupLength} width={200}>
            {moduleName}
          </TableCell>
        )}
        <TableCell
          width={150}
          align="right"
          className={clsx('complianceOperationItem', { [item.criticity]: displayCriticity })}
        >
          <ComplianceOperationAccordion
            displayText={item.complianceOperationItem.title}
            mode="score"
            operator={item.complianceOperationItem.operator}
            childrenValue={item.root.children}
            topicMap={topicMap}
            advancedAnalysisMap={advancedAnalysisMap}
            operationMap={complianceOperationMap}
          />
        </TableCell>
        <TableCell width={100} align="right" className={`${displayCriticity}`}>
          {totalFormat.format({ numerator: item.num ?? 0, denominator: item.denom ?? 0 })}
        </TableCell>
      </StyledRow>
    );
  }

  if (item.__typename === 'EvaluationSheetResultFactCheckModuleItem') {
    return (
      <StyledRow key={item.id}>
        {index === 0 && (
          <TableCell rowSpan={groupLength} width={200}>
            {moduleName}
          </TableCell>
        )}
        <TableCell
          width={150}
          align="right"
          className={clsx('factCheckModule', { [item.criticity]: displayCriticity })}
        >
          <FactCheckRunDetailsAccordion item={item} />
        </TableCell>
        <TableCell width={100} align="right" className={`${displayCriticity}`}>
          {totalFormat.format({ numerator: item.num ?? 0, denominator: item.denom ?? 0 })}
        </TableCell>
      </StyledRow>
    );
  }

  throw new Error(`Unsupported item type: ${(item as any).__typename}`);
}
